import type { TradeMarker } from '../../types/tradeMarkers';

const toFiniteNumber = (value: unknown): number | null => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : null;
};

const toUnixSeconds = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 10_000_000_000 ? Math.floor(value / 1000) : Math.floor(value);
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const asNumber = Number(value);
    if (Number.isFinite(asNumber)) {
      return asNumber > 10_000_000_000 ? Math.floor(asNumber / 1000) : Math.floor(asNumber);
    }
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return Math.floor(parsed / 1000);
  }

  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return Math.floor(value.getTime() / 1000);
  }

  return null;
};

const inferDirection = (trade: any, firstExecutionSide?: 'buy' | 'sell' | null): 'long' | 'short' => {
  const raw = String(trade?.direction ?? trade?.side ?? trade?.openingSide ?? '').toLowerCase();
  if (raw.includes('short') || raw.includes('sell')) return 'short';
  if (raw.includes('long') || raw.includes('buy')) return 'long';
  if (firstExecutionSide === 'sell') return 'short';
  return 'long';
};

const normalizeExecutionSide = (value: unknown): 'buy' | 'sell' | null => {
  const raw = String(value ?? '').toLowerCase();
  if (raw.includes('buy') || raw === 'b') return 'buy';
  if (raw.includes('sell') || raw === 's') return 'sell';
  return null;
};

export function buildTradeMarkers(trade: any): TradeMarker[] {
  if (!trade) return [];

  const executions = Array.isArray(trade?.executions)
    ? trade.executions
    : Array.isArray(trade?.fills)
      ? trade.fills
      : Array.isArray(trade?.executionsList)
        ? trade.executionsList
        : [];

  const firstExec = executions[0] ?? null;
  const lastExec = executions.length > 0 ? executions[executions.length - 1] : null;
  const firstExecutionSide = normalizeExecutionSide(firstExec?.side ?? firstExec?.action ?? firstExec?.type);

  const direction = inferDirection(trade, firstExecutionSide);

  const entryTime = toUnixSeconds(
    trade?.entryTime ?? trade?.openTime ?? trade?.openedAt ?? trade?.entryTimestamp ?? trade?.timestamp ?? firstExec?.time ?? firstExec?.timestamp ?? firstExec?.executedAt ?? firstExec?.date,
  );
  const exitTime = toUnixSeconds(
    trade?.exitTime ?? trade?.closeTime ?? trade?.closedAt ?? trade?.exitTimestamp ?? lastExec?.time ?? lastExec?.timestamp ?? lastExec?.executedAt ?? lastExec?.date,
  );

  const entryPrice = toFiniteNumber(trade?.entryPrice ?? trade?.avgEntryPrice ?? trade?.avgEntry ?? firstExec?.price ?? firstExec?.avgPrice);
  const exitPrice = toFiniteNumber(trade?.exitPrice ?? trade?.avgExitPrice ?? trade?.avgExit ?? lastExec?.price ?? lastExec?.avgPrice);

  const quantityRaw =
    trade?.quantity ??
    trade?.qty ??
    trade?.size ??
    trade?.volume ??
    firstExec?.qty ??
    firstExec?.quantity ??
    firstExec?.shares;
  const quantity = Math.max(0, Math.abs(toFiniteNumber(quantityRaw) ?? 0));

  const pnl = toFiniteNumber(trade?.pnl ?? trade?.calculatedPnl ?? trade?.realizedPnl ?? trade?.profitLoss ?? trade?.profit);
  const rMultiple = toFiniteNumber(trade?.rMultiple ?? trade?.r ?? trade?.r_mult ?? trade?.rMultipleValue);

  const markers: TradeMarker[] = [];

  if (entryTime != null && entryPrice != null) {
    markers.push({
      id: `entry-${entryTime}-${entryPrice}`,
      type: 'entry',
      direction,
      time: entryTime,
      price: entryPrice,
      quantity,
      pnl: pnl ?? undefined,
      rMultiple: rMultiple ?? undefined,
    });
  }

  if (exitTime != null && exitPrice != null) {
    markers.push({
      id: `exit-${exitTime}-${exitPrice}`,
      type: 'exit',
      direction,
      time: exitTime,
      price: exitPrice,
      quantity,
      pnl: pnl ?? undefined,
      rMultiple: rMultiple ?? undefined,
    });
  }

  return markers.sort((a, b) => a.time - b.time);
}
